import React, { useState, useEffect, useCallback } from 'react';
import { Dropzone } from '@unfold/dropzone';
import { Grid, GridItem } from '@unfold/grid';
import { Typography } from '@unfold/typography';
import { DropzoneWrapper, Divider, StyledLabel, ErrorMessage } from './styled';
import { Colors } from '@unfold/theme';
import { Button } from '@unfold/button';
import CircularProgressBar from './CircularProgressBar';
import { useBrandKitState } from '../../../store';
import { asyncForEach, formatBytes } from '@unfold/utils';
import { IFile, TFileRejection } from '../../types';

const MAX_SIZE_KB = 10485760;
interface IUploadModalProps {
  maxSize?: number;
  fileType: 'font' | 'image' | 'logo';
  dropzoneWidth?: number | string;
  dropzoneHeight?: number | string;
  onClose: () => void;
}
const getErrorMessageByCode = (filename: string, errorCode: string): string => {
  // Error codes
  const FILE_INVALID_TYPE = 'file-invalid-type'
  const FILE_TOO_LARGE = 'file-too-large'
  let message = `"${filename}"`;
  switch (errorCode) {
    case FILE_TOO_LARGE:
      message += ' is too big.';
      break;
    case FILE_INVALID_TYPE:
      message += ' is not supported.';
      break;
    default:
      message += ' cannot be uploaded.';
      break;
  }

  return message;
};

export const UploadModal: React.FC<IUploadModalProps> = (props) => {
  const [isUploadListVisible, setIsUploadListVisible] = useState<boolean>(
    false
  );
  const [isProgressVisible, setIsProgressVisible] = useState<boolean>(
    false
  );
  const [fileRejections, setFileRejections] = useState<TFileRejection[]>([]);
  const [state, actions] = useBrandKitState();

  const handleOnDrop = useCallback((files: IFile[]): void => {
    actions.setAcceptedFiles(files);
  }, []);

  const handleDropRejected = (fileRejections: TFileRejection[]): void => {
    setFileRejections(fileRejections);
  }

  const handleFileAdded = async (): Promise<void> => {
    setIsProgressVisible(true);

    await asyncForEach(state.acceptedFiles, async (file) => {
      await actions.addFile(file, props.fileType);
    });

    actions.load();
    props.onClose();
  };

  const handleCancel = (): void => {
    setIsUploadListVisible(false);
  };

  useEffect(() => {
    if (state.acceptedFiles.length) {
      setIsUploadListVisible(true);
    }
  }, [state.acceptedFiles]);

  // Unmount Effect
  useEffect(() => (): void => {
    actions.resetUploadState();
  }, []);

  return (
    <>
      {isUploadListVisible ? (
        <>
          <Grid verticalPadding={10} itemDirection="column">
            {state.acceptedFiles.map((file) => (
              <GridItem key={file.name}>
                <Grid>
                  <GridItem isStretch>{file.name}</GridItem>
                  <GridItem isWidthAuto>
                    {isProgressVisible &&
                      <CircularProgressBar progress={state.progressingFiles[file.name]} />
                    }
                  </GridItem>
                </Grid>
              </GridItem>
            ))}
          </Grid>
          <Grid horizontalPadding={20} css={{ marginTop: 20 }}>
            <GridItem isStretch>
              <Button
                appearance="primary"
                onClick={handleFileAdded}
                width="100%"
              >
                UPLOAD
              </Button>
            </GridItem>
            <GridItem isStretch>
              <Button appearance="bordered" onClick={handleCancel} width="100%">
                CANCEL
              </Button>
            </GridItem>
          </Grid>
        </>
      ) : (
        <>
          <Grid verticalPadding={10} itemDirection="column">
            <GridItem>
              <GridItem isStretch>
                <StyledLabel variant="Paragraph">Files supported</StyledLabel>
              </GridItem>
              <GridItem width="100px" justifyContent="flex-end" isWidthAuto>
                <Typography variant="Paragraph" fontSize={16}>
                  {props.fileType === 'font' ? 'OTF, TTF' : 'JPG, PNG, GIF'}
                </Typography>
              </GridItem>
            </GridItem>
            <GridItem>
              <GridItem isStretch>
                <StyledLabel variant="Paragraph">Total file size</StyledLabel>
              </GridItem>
              <GridItem width="100px" justifyContent="flex-end">
                <Typography variant="Paragraph" fontSize={16}>
                  {props.maxSize && formatBytes(props.maxSize)}
                </Typography>
              </GridItem>
            </GridItem>
          </Grid>
          <Divider />
          <StyledLabel variant="Paragraph">Drag and drop to upload</StyledLabel>
          <DropzoneWrapper>
            <Dropzone
              width={props.dropzoneWidth}
              height={props.dropzoneHeight}
              onDrop={handleOnDrop}
              onDropRejected={handleDropRejected}
              accept={
                props.fileType === 'font' ? '.ttf,.otf' : '.jpg,.jpeg,.png,.gif'
              }
              maxSize={props.maxSize}
            />
            <Typography
              variant="Paragraph"
              fontSize={12}
              customColor={{ light: Colors.R500, dark: Colors.R200 }}
            >
              {fileRejections.map((rejection) => {
                const filename = rejection.file.name;
                return rejection.errors.map((error) => {
                  return <ErrorMessage key={`${filename}-${error.code}`}>{getErrorMessageByCode(filename, error.code)}</ErrorMessage>;
                });
              })}
            </Typography>
          </DropzoneWrapper>
          {props.fileType === 'font' && (
            <>
              <Divider />
              <Typography
                css={{ marginBottom: 10 }}
                variant="H200"
                customColor={{ light: Colors.G300, dark: Colors.G600 }}
              >
                Font licencing
              </Typography>
              <Typography
                variant="Paragraph"
                fontSize={12}
                customColor={{ light: Colors.G200, dark: Colors.G600 }}
              >
                By uploading any font files, you represent and warrant that you
                own or otherwise have the valid right to use such files, and
                acknowledge that you have read and accepted our Terms of
                Service.
              </Typography>
            </>
          )}
        </>
      )}
    </>
  );
};

UploadModal.defaultProps = {
  maxSize: MAX_SIZE_KB,
};
